import { Outlet, useLocation } from 'react-router-dom';
import { ListIcon } from '@phosphor-icons/react';
import { toggleMobileMenu } from 'src/redux/ui';
import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import NavbarRefresh from 'src/components/layouts/navbar/NavbarRefresh';
import {
  NAVBAR_REFRESH_COLLAPSED_MARGIN_CLASS,
  NAVBAR_REFRESH_EXPANDED_MARGIN_CLASS
} from 'src/components/layouts/navbar/constants';
import { TenantSettingsModalProvider } from 'src/components/layouts/navbar/TenantSettingsModalContext';
import useAuth from 'src/hooks/useAuth';
import { MobileHeaderActionsProvider, useMobileHeaderActions } from 'src/components/layouts/MobileHeaderActionsContext';
import { useRightPanel } from 'src/components/layouts/RightPanelContext';
import { globalOnboarding } from 'src/features/onboarding/globalOnboarding';
import { useRightPanelControl } from 'src/hooks/useRightPanelControl';
import React, { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import Button from 'src/components/ui/Button';
import { NotificationModal, useNotifications } from 'src/features/notifications';
import { LiveRecordingProvider } from 'src/features/meetings/LiveRecordingContext';
import RecordingBanner from 'src/features/meetings/components/RecordingBanner';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { ChatListProvider } from 'src/features/chat/hooks/useChatList';
import { PageHeader, PageHeaderProvider } from 'src/components/layouts/PageHeader';
import { ActionViewerProvider } from 'src/features/groups/components/ActionViewer';

const NAVBAR_REFRESH_COLLAPSED_STORAGE_KEY = 'navbar-refresh-collapsed';

function useMdUp() {
  return useSyncExternalStore(
    (onStoreChange) => {
      const media = window.matchMedia('(min-width: 900px)');
      media.addEventListener('change', onStoreChange);
      return () => media.removeEventListener('change', onStoreChange);
    },
    () => window.matchMedia('(min-width: 900px)').matches,
    () => true
  );
}

const MobileHeader = ({
  specialBtn = null,
  showPageHeader = false
}: {
  specialBtn?: React.ReactNode;
  showPageHeader?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { actions: extraActions } = useMobileHeaderActions();
  return (
    <div className="fixed top-0 z-50 flex h-16 w-full items-center justify-between bg-[#34416F] p-3 md:hidden [&_button]:border-white/50 [&_button]:text-white [&_button:hover]:bg-white/10 [&_button_svg]:text-white">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <Button
          icon={ListIcon}
          variant="outline"
          size="md"
          onClick={() => dispatch(toggleMobileMenu())}
          aria-label="Open Menu"
        />

        {/* Extra actions injected through context */}
        {extraActions
          .filter((action) => action.position === 'left')
          .map((action, idx) => (
            <React.Fragment key={idx}>{action.action}</React.Fragment>
          ))}
        {showPageHeader ? <PageHeader variant="mobile" /> : null}
      </div>
      <div className="flex items-center gap-2 flex-row-reverse">
        {extraActions
          .filter((action) => action.position === 'right')
          .map((action, idx) => (
            <React.Fragment key={idx}>{action.action}</React.Fragment>
          ))}
      </div>
      {specialBtn}
    </div>
  );
};

const AppContent = ({ navbarRefreshCollapsed = false, showDesktopPageHeader = true }) => {
  const dispatch = useAppDispatch();
  const { isMobileMenuOpen } = useAppSelector((state: any) => state.ui);
  const { isOpen: isRightPanelOpen, panelWidth } = useRightPanel();
  const { showOnboarding, hideOnboarding } = useRightPanelControl();
  const { isAuthenticated, user, isInitialized } = useAuth();
  const liveTranscriptionEnabled = useFeatureFlag('transcription');
  const location = useLocation();
  const pageScrollerRef = useRef<HTMLDivElement>(null);
  const [pageScrolled, setPageScrolled] = useState(false);
  const currentMeetingId = useMemo(() => {
    const match = location.pathname.match(/^\/meetings\/([^/]+)/);
    return match?.[1];
  }, [location.pathname]);

  // Notification system
  const { notifications, close, dismiss, neverShow, acknowledge } = useNotifications();

  // Connect right panel control to global onboarding service
  useEffect(() => {
    globalOnboarding.setRightPanelControl({
      showOnboarding,
      hideOnboarding
    });
  }, [showOnboarding, hideOnboarding]);

  // Check if onboarding is completed and auto-trigger if not
  useEffect(() => {
    // Only run onboarding logic after PostHog identification is complete
    if (isAuthenticated && isInitialized) {
      // DISABLED: Don't auto-show onboarding panel - let users open it manually
      // if (!onboardingCompleted && user?.getActiveTenantRole?.(location.pathname) !== 'guest') {
      //   globalOnboarding.showOnboarding();
      // }
    }
  }, [isAuthenticated, isInitialized, user]);

  // Global onboarding triggers based on URL or other conditions
  // TODO: review these triggers.
  useEffect(() => {
    const handleGlobalOnboarding = () => {
      const searchParams = new URLSearchParams(window.location.search);

      // Check for onboarding trigger in URL
      if (searchParams.get('onboarding') === 'true') {
        globalOnboarding.showOnboarding();
        // Clean up URL
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('onboarding');
        window.history.replaceState({}, '', newUrl.toString());
      }

      // Check for onboarding trigger in localStorage (set by other parts of the app)
      const pendingOnboarding = localStorage.getItem('pendingOnboarding');
      if (pendingOnboarding) {
        const { timestamp } = JSON.parse(pendingOnboarding);
        const now = Date.now();

        // Only show if it's been less than 5 minutes
        if (now - timestamp < 5 * 60 * 1000) {
          globalOnboarding.showOnboarding();
          localStorage.removeItem('pendingOnboarding');
        }
      }
    };

    handleGlobalOnboarding();
  }, []);

  useEffect(() => {
    const scroller = pageScrollerRef.current;
    if (!scroller) return;
    scroller.scrollTop = 0;
    setPageScrolled(false);

    const update = () => {
      const overflow = scroller.scrollHeight - scroller.clientHeight;
      setPageScrolled(overflow >= 8 && scroller.scrollTop > 8);
    };

    update();
    scroller.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(scroller);
    // The scroller's own box does not change when a chat is replaced by the
    // shorter new-chat screen, so also watch the page content.
    if (scroller.firstElementChild) observer.observe(scroller.firstElementChild);
    return () => {
      scroller.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [location.pathname]);

  return (
    <div
      className={`flex h-dvh flex-col md:flex-row ${
        navbarRefreshCollapsed ? NAVBAR_REFRESH_COLLAPSED_MARGIN_CLASS : NAVBAR_REFRESH_EXPANDED_MARGIN_CLASS
      } pt-16 md:pt-0`}
    >
      {/* Mobile Menu overlay, display only when mobile menu is open */}
      {isMobileMenuOpen && (
        <div
          className="absolute inset-0 top-16 z-[998] bg-black/50 transition-opacity duration-300 ease-in-out"
          onClick={() => dispatch(toggleMobileMenu(false))}
        />
      )}

      {/* Main content - shift left when right panel is open */}
      <main
        className="flex h-full min-h-0 min-w-0 flex-1 flex-col transition-all duration-300"
        style={{ marginRight: isRightPanelOpen ? panelWidth : 0 }}
      >
        {liveTranscriptionEnabled && <RecordingBanner currentMeetingId={currentMeetingId} />}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="mx-auto w-full min-w-0 shrink-0">{showDesktopPageHeader ? <PageHeader /> : null}</div>
          <div className="relative flex h-full min-h-0 min-w-0 flex-1 flex-col">
            <div
              aria-hidden
              className={`pointer-events-none absolute inset-x-0 top-0 z-10 h-px bg-border-subtle transition-opacity ${
                pageScrolled ? 'opacity-100' : 'opacity-0'
              }`}
            />
            <div ref={pageScrollerRef} className="custom-scrollbar h-full min-h-0 min-w-0 flex-1 overflow-y-auto">
              <Outlet />
            </div>
          </div>
        </div>
      </main>

      {/* Notification Modal - Show the first notification in queue */}
      {notifications.length > 0 && (
        <NotificationModal
          notification={notifications[0]}
          onClose={() => close(notifications[0].id)}
          onAcknowledgement={() => acknowledge(notifications[0].id)}
          onDismiss={() => dismiss(notifications[0].id)}
          onNeverShow={() => neverShow(notifications[0].id)}
        />
      )}
    </div>
  );
};

export default function AppLayout() {
  const mdUp = useMdUp();
  const [navbarRefreshCollapsed, setNavbarRefreshCollapsed] = useState(
    () =>
      typeof window !== 'undefined' && window.sessionStorage.getItem(NAVBAR_REFRESH_COLLAPSED_STORAGE_KEY) === 'true'
  );
  const handleToggleNavbarRefresh = useCallback(() => {
    setNavbarRefreshCollapsed((isCollapsed) => {
      const nextIsCollapsed = !isCollapsed;
      window.sessionStorage.setItem(NAVBAR_REFRESH_COLLAPSED_STORAGE_KEY, String(nextIsCollapsed));
      return nextIsCollapsed;
    });
  }, []);

  return (
    <div className="relative min-h-screen bg-navbar">
      <LiveRecordingProvider>
        <ActionViewerProvider>
          <TenantSettingsModalProvider>
            <ChatListProvider>
              <MobileHeaderActionsProvider>
                <PageHeaderProvider>
                  <MobileHeader showPageHeader={!mdUp} />
                  <NavbarRefresh isCollapsed={navbarRefreshCollapsed} onToggleCollapsed={handleToggleNavbarRefresh} />
                  <AppContent navbarRefreshCollapsed={navbarRefreshCollapsed} showDesktopPageHeader={mdUp} />
                </PageHeaderProvider>
              </MobileHeaderActionsProvider>
            </ChatListProvider>
          </TenantSettingsModalProvider>
        </ActionViewerProvider>
      </LiveRecordingProvider>
    </div>
  );
}
